/**
 * Marketplace home + Search + Filter/Sort sheets + ProductCard.
 * One file to keep wiring simple and reduce component noise.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, ShoppingCart, Heart, Plus, SlidersHorizontal, ArrowUpDown, X, BadgeCheck, ChevronLeft } from "lucide-react";
import { apiClient } from "../../services/api";
import BottomNav from "../../components/layout/BottomNav";
import { toast } from "sonner";
import { useAuthStore } from "../../store/authStore";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "equipment", label: "Equipment" },
  { id: "supplements", label: "Supplements" },
  { id: "diet_food", label: "Diet Food" },
  { id: "merchandise", label: "Merchandise" },
  { id: "clothing", label: "Clothing" },
];

const SORTS = [
  { id: "relevance", label: "Relevance" },
  { id: "price_asc", label: "Price: Low to High" },
  { id: "price_desc", label: "Price: High to Low" },
  { id: "newest", label: "Newest" },
];

// ──────────────────────────────────────────────────────────────
// PRODUCT CARD — exported, reused everywhere
// ──────────────────────────────────────────────────────────────
export function ProductCard({ product, onSave, onAddToCart, onOpen, compact = false }) {
  const discount = product.original_price_inr && product.original_price_inr > product.price_inr;
  const conditionLabel = product.condition
    ? { new: "New", like_new: "Like New", good: "Good", fair: "Fair" }[product.condition] || null
    : null;
  return (
    <div
      className="bg-white border border-line2 rounded-[14px] overflow-hidden flex flex-col relative"
      data-testid={`product-card-${product.product_id}`}
      style={{ width: compact ? 140 : undefined }}
    >
      <div
        onClick={() => onOpen?.(product)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen?.(product)}
        className="relative aspect-square w-full bg-line2 overflow-hidden cursor-pointer"
        data-testid={`product-photo-${product.product_id}`}
      >
        <img
          src={(product.photos || [])[0] || ""}
          alt={product.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {conditionLabel && (
          <span
            className="absolute top-1.5 left-1.5 uppercase font-bold tracking-wider"
            style={{ background: "rgba(12,12,11,0.65)", color: "#fff", fontSize: 9, padding: "3px 6px", borderRadius: 6 }}
          >
            {conditionLabel}
          </span>
        )}
        {product.seller_type === "brand" && (
          <span
            className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 uppercase font-bold"
            style={{ background: "rgba(12,12,11,0.65)", color: "#fff", fontSize: 9, padding: "3px 6px", borderRadius: 6 }}
          >
            <BadgeCheck size={9} /> Brand
          </span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onSave?.(product); }}
          data-testid={`product-save-${product.product_id}`}
          aria-label="Save"
          className="absolute top-1.5 right-1.5 w-9 h-9 rounded-full grid place-items-center"
          style={{ background: "rgba(240,238,234,0.85)" }}
        >
          <Heart size={16} fill={product.is_saved ? "#00D4FF" : "transparent"} color={product.is_saved ? "#00D4FF" : "#1A1A1A"} />
        </button>
      </div>
      <div className="px-2.5 pt-2 pb-3 flex flex-col gap-0.5 flex-1">
        <span className="text-[9px] font-bold uppercase tracking-[0.08em] text-ink-tertiary">
          {product.category.replace("_", " ")}
        </span>
        <button
          onClick={() => onOpen?.(product)}
          className="text-left font-bold text-[13.5px] leading-tight line-clamp-2"
          style={{ color: "#1A1A1A" }}
        >
          {product.title}
        </button>
        <div className="flex items-baseline gap-1.5 mt-0.5">
          <span className="font-barlow-cond font-extrabold text-[18px]" style={{ color: "#1A1A1A" }}>
            ₹{product.price_inr.toLocaleString("en-IN")}
          </span>
          {discount && (
            <span className="text-[11px] text-ink-tertiary line-through">₹{product.original_price_inr.toLocaleString("en-IN")}</span>
          )}
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-[10px] text-ink-tertiary font-medium truncate max-w-[55%]">
            {product.delivery_available ? "All India" : product.city}
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); onAddToCart?.(product); }}
            data-testid={`product-add-${product.product_id}`}
            className="h-6 px-2.5 rounded-pill text-[10px] font-bold"
            style={{ background: "#00D4FF", color: "#0B0B0B", borderRadius: 100 }}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Filter + Sort bottom sheets
// ──────────────────────────────────────────────────────────────
function BottomSheet({ children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-end bg-black/40" onClick={onClose} data-testid={testid}>
      <div
        className="w-full bg-white rounded-t-[24px] p-5 max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 20px)" }}
      >
        <div className="w-10 h-1 bg-line2 rounded-full mx-auto mb-4" />
        {children}
      </div>
    </div>
  );
}

function SortSheet({ value, onChange, onClose }) {
  return (
    <BottomSheet onClose={onClose} testid="sort-sheet">
      <h3 className="font-display text-[20px] mb-3">Sort by</h3>
      <div className="space-y-1">
        {SORTS.map((s) => (
          <button
            key={s.id}
            onClick={() => { onChange(s.id); onClose(); }}
            data-testid={`sort-${s.id}`}
            className="w-full flex items-center justify-between h-12 px-3 rounded-[12px] hover:bg-canvas2 text-left"
          >
            <span className="text-[14px] font-medium">{s.label}</span>
            {value === s.id && <div className="w-2 h-2 rounded-full" style={{ background: "#00D4FF" }} />}
          </button>
        ))}
      </div>
    </BottomSheet>
  );
}

function FilterSheet({ filters, onApply, onReset, onClose }) {
  const [f, setF] = useState(filters);
  const setField = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  return (
    <BottomSheet onClose={onClose} testid="filter-sheet">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-[20px]">Filters</h3>
        <button onClick={onClose} aria-label="Close" className="w-9 h-9 grid place-items-center"><X size={18} /></button>
      </div>

      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-wider text-ink-tertiary font-medium mb-2">Price Range</div>
        <div className="flex gap-2 mb-2">
          <input type="number" placeholder="Min ₹" value={f.min_price || ""} onChange={(e) => setField("min_price", e.target.value ? Number(e.target.value) : null)} data-testid="filter-min-price" className="flex-1 h-11 px-3 rounded-[10px] border border-line bg-canvas2 outline-none text-[14px]" />
          <input type="number" placeholder="Max ₹" value={f.max_price || ""} onChange={(e) => setField("max_price", e.target.value ? Number(e.target.value) : null)} data-testid="filter-max-price" className="flex-1 h-11 px-3 rounded-[10px] border border-line bg-canvas2 outline-none text-[14px]" />
        </div>
        <div className="flex flex-wrap gap-2">
          {[[0,500,"Under ₹500"],[500,2000,"₹500–2000"],[2000,5000,"₹2000–5000"],[5000,null,"Above ₹5000"]].map(([mn,mx,l]) => (
            <button key={l} onClick={() => setF({ ...f, min_price: mn, max_price: mx })} data-testid={`preset-${l.replace(/[^a-z0-9]/gi,"").toLowerCase()}`} className="h-8 px-3 rounded-pill text-[12px] font-medium" style={{ background: "#ECEAE7", borderRadius: 100 }}>{l}</button>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-wider text-ink-tertiary font-medium mb-2">Seller</div>
        <div className="flex gap-2">
          {[["individual","Individuals"],["brand","Brands"]].map(([id,l]) => {
            const on = f.seller_type === id;
            return (
              <button key={id} onClick={() => setField("seller_type", on ? null : id)} data-testid={`seller-${id}`} className="flex-1 h-10 rounded-pill text-[13px]" style={{ background: on ? "#00D4FF" : "#ECEAE7", color: on ? "#0B0B0B" : "#6B6760", fontWeight: on ? 700 : 600, borderRadius: 100 }}>{l}</button>
            );
          })}
        </div>
      </div>

      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-wider text-ink-tertiary font-medium mb-2">Delivery</div>
        <button onClick={() => setField("delivery_available", !f.delivery_available)} data-testid="filter-delivery" className="w-full h-10 rounded-pill text-[13px]" style={{ background: f.delivery_available ? "#00D4FF" : "#ECEAE7", color: f.delivery_available ? "#0B0B0B" : "#6B6760", fontWeight: f.delivery_available ? 700 : 600, borderRadius: 100 }}>
          Delivery available {f.delivery_available ? "✓" : ""}
        </button>
      </div>

      <div className="flex gap-2 mt-6">
        <button onClick={() => { onReset(); onClose(); }} data-testid="filter-reset" className="flex-1 h-12 rounded-pill border border-line2 font-medium" style={{ borderRadius: 100 }}>Reset</button>
        <button onClick={() => { onApply(f); onClose(); }} data-testid="filter-apply" className="flex-1 h-12 rounded-pill font-bold" style={{ background: "#00D4FF", color: "#0B0B0B", borderRadius: 100 }}>Apply Filters</button>
      </div>
    </BottomSheet>
  );
}

// ──────────────────────────────────────────────────────────────
// Marketplace home
// ──────────────────────────────────────────────────────────────
export default function Marketplace() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [searchParams] = useSearchParams();
  const initialCat = searchParams.get("category") || "all";

  const [products, setProducts] = useState([]);
  const [banner, setBanner] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [category, setCategory] = useState(initialCat);
  const [sort, setSort] = useState("relevance");
  const [filters, setFilters] = useState({ min_price: null, max_price: null, seller_type: null, delivery_available: false });
  const [loading, setLoading] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [showSort, setShowSort] = useState(false);
  const [showFilter, setShowFilter] = useState(false);

  const params = useMemo(() => {
    const p = { page, size: 20, sort, category: category !== "all" ? category : undefined };
    if (filters.min_price != null) p.min_price = filters.min_price;
    if (filters.max_price != null) p.max_price = filters.max_price;
    if (filters.seller_type) p.seller_type = filters.seller_type;
    if (filters.delivery_available) p.delivery_available = true;
    return p;
  }, [page, sort, category, filters]);

  const load = async (reset = false) => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/marketplace/products", { params: { ...params, page: reset ? 1 : page } });
      setProducts((prev) => (reset ? data.products : [...prev, ...data.products]));
      setHasMore(data.has_more);
      if (reset) setPage(1);
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Reset list whenever filters/sort/category change
    setPage(1);
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, category, filters.min_price, filters.max_price, filters.seller_type, filters.delivery_available]);

  useEffect(() => {
    apiClient.get("/marketplace/context-banner?limit=5").then((r) => setBanner(r.data)).catch(() => setBanner(null));
    if (user) refreshCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const refreshCart = async () => {
    if (!user) return;
    try {
      const { data } = await apiClient.get("/marketplace/cart");
      setCartCount((data.items || []).length);
    } catch {}
  };

  const toggleSave = async (p) => {
    if (!user) { navigate("/login"); return; }
    try {
      const { data } = await apiClient.post("/marketplace/saves/toggle", { product_id: p.product_id });
      setProducts((prev) => prev.map((x) => x.product_id === p.product_id ? { ...x, is_saved: data.saved } : x));
      toast.success(data.saved ? "Saved" : "Removed from saves");
    } catch { toast.error("Could not save"); }
  };

  const addToCart = async (p) => {
    if (!user) { navigate("/login"); return; }
    try {
      await apiClient.post("/marketplace/cart", { product_id: p.product_id, quantity: 1 });
      toast.success(`${p.title} added to cart`);
      refreshCart();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };

  return (
    <div className="app-page min-h-screen" data-testid="marketplace-page" style={{ background: "#F4F3F1" }}>
      {/* Top bar */}
      <header className="sticky top-0 z-20 bg-canvas/90 backdrop-blur-xl">
        <div className="max-w-2xl mx-auto h-14 px-4 flex items-center justify-between">
          <h1 className="font-barlow-cond font-black" style={{ fontSize: 26, lineHeight: 1, color: "#1A1A1A" }}>Shop</h1>
          <div className="flex items-center gap-1">
            <button onClick={() => navigate("/marketplace/search")} aria-label="Search" data-testid="market-search-btn" className="w-11 h-11 grid place-items-center rounded-full hover:bg-white/60">
              <Search size={20} />
            </button>
            <button onClick={() => navigate("/marketplace/cart")} aria-label="Cart" data-testid="market-cart-btn" className="w-11 h-11 grid place-items-center rounded-full hover:bg-white/60 relative">
              <ShoppingCart size={20} />
              {cartCount > 0 && (
                <span className="absolute top-1 right-1 min-w-[18px] h-[18px] text-[10px] font-bold rounded-full grid place-items-center px-1" style={{ background: "#00D4FF", color: "#000" }} data-testid="cart-badge">{cartCount}</span>
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto pb-28">
        {/* Context banner */}
        {banner && banner.products && banner.products.length > 0 && (
          <section className="mx-4 mt-3 mb-4 bg-white border border-line2 rounded-[16px] p-4" data-testid="context-banner">
            <div className="font-display text-[18px] leading-tight">{banner.headline}</div>
            <div className="text-[12px] text-ink-soft mb-3">{banner.sub}</div>
            <div className="flex gap-2.5 overflow-x-auto -mx-1 px-1" style={{ scrollbarWidth: "none" }}>
              {banner.products.slice(0, 5).map((p) => (
                <div key={p.product_id} className="shrink-0">
                  <ProductCard product={p} compact onOpen={(x) => navigate(`/marketplace/product/${x.product_id}`)} onAddToCart={addToCart} onSave={toggleSave} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Category pills */}
        <div className="flex gap-2 px-4 pb-3 overflow-x-auto" style={{ scrollbarWidth: "none" }} data-testid="category-row">
          {CATEGORIES.map((c) => {
            const on = category === c.id;
            return (
              <button key={c.id} onClick={() => setCategory(c.id)} data-testid={`cat-${c.id}`} className="shrink-0 h-9 px-4 rounded-pill text-[12.5px]" style={{ background: on ? "#00D4FF" : "#ECEAE7", color: on ? "#0B0B0B" : "#6B6760", fontWeight: on ? 700 : 600, borderRadius: 100 }}>{c.label}</button>
            );
          })}
        </div>

        {/* Sort + filter */}
        <div className="flex items-center justify-between px-4 pb-3">
          <button onClick={() => setShowSort(true)} data-testid="open-sort" className="flex items-center gap-1.5 text-[13px] font-semibold text-ink-soft">
            <ArrowUpDown size={14} /> Sort: {SORTS.find((s) => s.id === sort)?.label}
          </button>
          <button onClick={() => setShowFilter(true)} data-testid="open-filter" className="flex items-center gap-1.5 text-[13px] font-semibold text-ink-soft">
            <SlidersHorizontal size={14} /> Filter
          </button>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 gap-2.5 px-4" data-testid="product-grid">
          {products.map((p) => (
            <ProductCard key={p.product_id} product={p} onOpen={(x) => navigate(`/marketplace/product/${x.product_id}`)} onAddToCart={addToCart} onSave={toggleSave} />
          ))}
        </div>

        {products.length === 0 && !loading && (
          <div className="text-center py-16 px-8" data-testid="market-empty">
            <div className="font-barlow-cond text-[22px] font-bold">Nothing here yet</div>
            <div className="text-[14px] text-ink-soft mt-2 mb-5">Be the first to list in this category</div>
            <button onClick={() => navigate("/marketplace/sell")} className="px-5 h-11 rounded-pill font-bold" style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}>List an item</button>
          </div>
        )}

        {hasMore && products.length > 0 && (
          <div className="text-center mt-5">
            <button onClick={() => { setPage(page + 1); load(false); }} disabled={loading} data-testid="load-more" className="px-5 h-10 rounded-pill text-[13px] font-medium border border-line2" style={{ borderRadius: 100 }}>
              {loading ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </main>

      {/* Sell FAB */}
      <button onClick={() => navigate("/marketplace/sell")} data-testid="sell-fab" aria-label="Sell" className="fixed bottom-[88px] right-4 z-30 h-12 px-5 rounded-pill flex items-center gap-1.5 font-bold text-[13px] shadow-lg" style={{ background: "#00D4FF", color: "#000", borderRadius: 100, boxShadow: "0 8px 24px rgba(0,212,255,0.4)" }}>
        <Plus size={18} /> Sell
      </button>

      <BottomNav />

      {showSort && <SortSheet value={sort} onChange={setSort} onClose={() => setShowSort(false)} />}
      {showFilter && <FilterSheet filters={filters} onApply={setFilters} onReset={() => setFilters({ min_price: null, max_price: null, seller_type: null, delivery_available: false })} onClose={() => setShowFilter(false)} />}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Search page
// ──────────────────────────────────────────────────────────────
const POPULAR = ["Dumbbells", "Whey protein", "Yoga mat", "Resistance band", "Creatine", "Running shoes"];

export function MarketplaceSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ff_market_recent_searches") || "[]"); } catch { return []; }
  });
  const [busy, setBusy] = useState(false);

  const run = async (q) => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const { data } = await apiClient.get("/marketplace/products", { params: { q: q.trim(), size: 40 } });
      setResults(data.products);
      const next = [q.trim(), ...recent.filter((r) => r !== q.trim())].slice(0, 5);
      setRecent(next);
      localStorage.setItem("ff_market_recent_searches", JSON.stringify(next));
    } finally { setBusy(false); }
  };

  return (
    <div className="app-page min-h-screen" data-testid="market-search-page" style={{ background: "#F4F3F1" }}>
      <header className="sticky top-0 z-20 bg-canvas/90 backdrop-blur-xl">
        <div className="max-w-2xl mx-auto h-14 px-3 flex items-center gap-2">
          <button onClick={() => navigate(-1)} aria-label="Back" data-testid="search-back" className="w-10 h-10 grid place-items-center"><ChevronLeft size={22} /></button>
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run(query)}
            placeholder="Search equipment, protein…"
            data-testid="search-input"
            className="flex-1 h-10 px-3 rounded-pill border border-line bg-white outline-none text-[14px]"
          />
          {query && <button onClick={() => setQuery("")} aria-label="Clear" className="w-9 h-9 grid place-items-center"><X size={16} /></button>}
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 pb-24">
        {!query && recent.length > 0 && (
          <section className="mt-4" data-testid="recent-searches">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[12px] font-semibold text-ink-soft">Recent</div>
              <button onClick={() => { setRecent([]); localStorage.removeItem("ff_market_recent_searches"); }} className="text-[12px] underline text-ink-tertiary">Clear</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {recent.map((r) => (
                <button key={r} onClick={() => { setQuery(r); run(r); }} data-testid={`recent-${r}`} className="h-9 px-3 rounded-pill text-[13px]" style={{ background: "#ECEAE7", borderRadius: 100 }}>{r}</button>
              ))}
            </div>
          </section>
        )}

        {!query && (
          <section className="mt-5">
            <div className="text-[12px] font-semibold text-ink-soft mb-2">Popular</div>
            <div className="flex flex-wrap gap-2">
              {POPULAR.map((t) => (
                <button key={t} onClick={() => { setQuery(t); run(t); }} data-testid={`popular-${t.replace(/\s/g,"").toLowerCase()}`} className="h-9 px-3 rounded-pill text-[13px] border border-line2" style={{ borderRadius: 100 }}>{t}</button>
              ))}
            </div>
          </section>
        )}

        {query && (
          <section className="mt-4">
            {busy && <div className="text-center text-ink-soft text-[13px] py-4">Searching…</div>}
            {!busy && results.length === 0 && (
              <div className="text-center py-10" data-testid="no-results">
                <div className="font-barlow-cond text-[22px] font-bold">No results for "{query}"</div>
                <button onClick={() => navigate("/marketplace/sell")} className="mt-4 px-5 h-11 rounded-pill font-bold" style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}>List this item for sale</button>
              </div>
            )}
            {!busy && results.length > 0 && (
              <>
                <div className="text-[13px] text-ink-soft mb-3">{results.length} results for "{query}"</div>
                <div className="grid grid-cols-2 gap-2.5">
                  {results.map((p) => (
                    <ProductCard key={p.product_id} product={p} onOpen={(x) => navigate(`/marketplace/product/${x.product_id}`)} onAddToCart={() => {}} onSave={() => {}} />
                  ))}
                </div>
              </>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
